import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, Star, MoreVertical, Shield,
  ThumbsUp, Mail, MessageSquare, Flag, AlertTriangle,
  User, Check, X, Edit, Trash2, Eye, FileText,
  ChevronDown, ChevronUp, Download, Save, Loader2,
  ChevronLeft, ChevronRight
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { fetchReviews, updateReview, deleteReview } from "../../redux/Slices/reviewSlice";
import { Review as ReviewType, SortReviewDto, ReviewStatusEnum } from "../../modules/shared/model";
import VoteDisplayButton from "../../components/ui/VoteDisplayButton";

const ReviewsAndRatings = () => {
  const dispatch = useAppDispatch();
  const { items: reviews, loading, error, totalCount, hasNextPage } = useAppSelector((state) => state.reviews);

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRating, setSelectedRating] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [showReviewDetails, setShowReviewDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsPerPage] = useState(10);
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());

  // Handle vote submission
  const handleVote = async (reviewId: string, voteType: "helpful" | "unhelpful") => {
    try {
      // You would typically call an API here to submit the vote
      console.log(`Voting ${voteType} for review ${reviewId}`);
      // Example: await dispatch(updateReviewVote({ reviewId, voteType }));
    } catch (error) {
      console.error("Error submitting vote:", error);
    }
  };

  // Fetch reviews on component mount
  useEffect(() => {
    const getSortConfig = (): SortReviewDto[] => {
      switch (sortBy) {
        case "newest":
          return [{ orderBy: "createdAt", order: "DESC" }];
        case "oldest":
          return [{ orderBy: "createdAt", order: "ASC" }];
        case "rating-high":
          return [{ orderBy: "rating", order: "DESC" }];
        case "rating-low":
          return [{ orderBy: "rating", order: "ASC" }];
        default:
          return [{ orderBy: "createdAt", order: "DESC" }];
      }
    };

    dispatch(fetchReviews({
      page: currentPage,
      limit: reviewsPerPage,
      sort: getSortConfig()
    }));
  }, [dispatch, currentPage, reviewsPerPage, sortBy]);

  // Helper function to get status name from status ID
  const getStatusName = (statusId: any): string => {
    const statusNumber = Number(statusId);
    switch (statusNumber) {
      case ReviewStatusEnum.Active:
        return "active";
      case ReviewStatusEnum.Archived:
        return "archived";
      case ReviewStatusEnum.Deleted:
        return "deleted";
      case ReviewStatusEnum.Inactive:
        return "inactive";
      default:
        return "unknown";
    }
  };

  // Helper function to get status ID from status name
  const getStatusId = (statusName: string): ReviewStatusEnum => {
    switch (statusName) {
      case "active":
        return ReviewStatusEnum.Active;
      case "archived":
        return ReviewStatusEnum.Archived;
      case "deleted":
        return ReviewStatusEnum.Deleted;
      case "inactive":
        return ReviewStatusEnum.Inactive;
      default:
        return ReviewStatusEnum.Active;
    }
  };

  // Transform API review to display format
  const transformReviewForDisplay = (review: ReviewType) => {
    const authorName = review.author?.firstName && review.author?.lastName
      ? `${review.author.firstName} ${review.author.lastName}`.trim()
      : review.author?.id || "Unknown User";

    const userBeingReviewed = review.user?.firstName && review.user?.lastName
      ? `${review.user.firstName} ${review.user.lastName}`.trim()
      : review.user?.id || "Unknown User";

    return {
      id: review.id,
      user: {
        name: authorName,
        avatar: review.author?.photo?.path || null,
        verified: true, // Assume verified for now
        totalReviews: 0, // This would need to be fetched separately
        email: (review.author as any)?.email || null
      },
      provider: {
        name: userBeingReviewed,
        service: review.milestone?.id ? `Milestone ${review.milestone.id}` : "Service",
        type: "Service",
        email: (review.user as any)?.email || null
      },
      author: {
        email: (review.author as any)?.email || null
      },
      rating: review.rating || 0,
      title: "Service Review",
      content: review.text || "No comment provided",
      date: review.createdAt,
      status: getStatusName(review.status?.id), // Get proper status name
      helpful: review.helpful || 0,
      reported: false, // This would need to be determined from review data
      attachments: {
        images: 0,
        videos: 0
      },
      serviceDetails: {
        date: new Date(review.createdAt).toLocaleDateString(),
        orderId: review.milestone?.id.slice(0, 8) || "N/A",
        cost: "$" + review.milestone?.amount
      },
      adminNotes: review.adminNotes || []
    };
  };

  // Use real reviews or fallback to mock data
  const allReviews = reviews && reviews.length > 0 ? reviews.map(transformReviewForDisplay) : [];

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1) {
      setCurrentPage(page);
    }
  };

  // Handle review text expansion
  const toggleReviewExpansion = (reviewId: string) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  // Calculate pagination with fallback for hasNextPage
  const totalPages = Math.ceil((totalCount || 0) / reviewsPerPage);
  const shouldShowPagination = totalCount > reviewsPerPage;
  const hasPreviousPage = currentPage > 1;
  const hasNextPageCalculated = hasNextPage ?? (currentPage * reviewsPerPage < (totalCount || 0));

  // Debug logging
  console.log("Rating.tsx Debug:", {
    totalCount,
    hasNextPage,
    hasNextPageCalculated,
    currentPage,
    reviewsPerPage,
    totalPages,
    shouldShowPagination
  });

  // Filter reviews based on search and filter criteria
  const displayReviews = allReviews.filter(review => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        review.user.name.toLowerCase().includes(searchLower) ||
        review.provider.name.toLowerCase().includes(searchLower) ||
        review.content.toLowerCase().includes(searchLower) ||
        review.title.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Rating filter
    if (selectedRating !== "all") {
      const rating = parseInt(selectedRating);
      if (review.rating !== rating) return false;
    }

    // Status filter
    if (selectedStatus !== "all") {
      if (review.status !== selectedStatus) return false;
    }

    return true;
  });

  // Review Details Modal Component
  const ReviewDetailsModal = ({ review, isOpen, onClose }) => {
    const [adminNote, setAdminNote] = useState("");
    const [selectedStatus, setSelectedStatus] = useState(review?.status || "active");
    const [isUpdating, setIsUpdating] = useState(false);

    // Handle status update
    const handleStatusUpdate = async () => {
      if (!review) return;
      
      setIsUpdating(true);
      try {
        await dispatch(updateReview({
          id: review.id,
          data: {
            status: { id: getStatusId(selectedStatus) }
          }
        }));
        
        // Fetch updated reviews after successful update
        dispatch(fetchReviews({
          page: currentPage,
          limit: reviewsPerPage,
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }));
        
        onClose();
      } catch (error) {
        console.error("Failed to update review status:", error);
      } finally {
        setIsUpdating(false);
      }
    };

    // Handle delete review
    const handleDeleteReview = async () => {
      if (!review) return;
      
      // Show confirmation dialog
      const confirmed = window.confirm(
        "Are you sure you want to permanently delete this review?\n\nThis action cannot be undone."
      );
      
      if (!confirmed) return;
      
      setIsUpdating(true);
      try {
        await dispatch(deleteReview(review.id));
        
        // Fetch updated reviews after successful deletion
        dispatch(fetchReviews({
          page: currentPage,
          limit: reviewsPerPage,
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }));
        
        onClose();
      } catch (error) {
        console.error("Failed to delete review:", error);
        alert("Failed to delete review. Please try again.");
      } finally {
        setIsUpdating(false);
      }
    };

    // Handle adding admin note
    const handleAddAdminNote = async () => {
      if (!adminNote.trim() || !review) return;
      
      setIsUpdating(true);
      try {
        const currentNotes = review.adminNotes || [];
        await dispatch(updateReview({
          id: review.id,
          data: {
            adminNotes: [...currentNotes, adminNote.trim()]
          }
        }));
        
        // Fetch updated reviews after successful note addition
        dispatch(fetchReviews({
          page: currentPage,
          limit: reviewsPerPage,
          sort: [{ orderBy: "createdAt", order: "DESC" }]
        }));
        
        setAdminNote("");
        onClose();
      } catch (error) {
        console.error("Failed to add admin note:", error);
      } finally {
        setIsUpdating(false);
      }
    };

    // Handle contact customer (client) via email
    const handleContactCustomer = () => {
      // Get the customer email from the review data
      // Note: Email might not be available in UserRef, so we'll handle this gracefully
      const customerEmail = (review.user as any)?.email || (review.author as any)?.email;
      
      if (customerEmail) {
        const subject = encodeURIComponent(`Regarding your review - Review ID: ${review.id}`);
        const body = encodeURIComponent(`Dear ${review.user.name || "Valued Customer"},\n\nWe are contacting you regarding your review for ${review.provider.name}.\n\nReview ID: ${review.id}\nService: ${review.provider.service}\nDate: ${new Date(review.date).toLocaleDateString()}\n\nPlease let us know if you have any questions or concerns.\n\nBest regards,\nKokobeo Support Team`);
        
        window.open(`mailto:${customerEmail}?subject=${subject}&body=${body}`, "_blank");
      } else {
        alert("Customer email not available. Please contact support for assistance.");
      }
    };

    // Handle contact provider via email
    const handleContactProvider = () => {
      // Get the provider email from the review data
      // Note: Email might not be available in UserRef, so we'll handle this gracefully
      const providerEmail = (review.provider as any)?.email || (review.user as any)?.email;
      
      if (providerEmail) {
        const subject = encodeURIComponent(`Regarding review about your service - Review ID: ${review.id}`);
        const body = encodeURIComponent(`Dear ${review.provider.name || "Service Provider"},\n\nWe are contacting you regarding a review about your service.\n\nReview ID: ${review.id}\nService: ${review.provider.service}\nCustomer: ${review.user.name}\nDate: ${new Date(review.date).toLocaleDateString()}\n\nPlease let us know if you have any questions or concerns.\n\nBest regards,\nKokobeo Support Team`);
        
        window.open(`mailto:${providerEmail}?subject=${subject}&body=${body}`, "_blank");
      } else {
        alert("Provider email not available. Please contact support for assistance.");
      }
    };

    if (!review) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b sticky top-0 bg-white" style={{ textAlign: "left" }}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Review Details</h2>
                  <p className="text-sm text-gray-500">ID: {review.id}</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6" style={{ textAlign: "left" }}>
              {/* Review Status */}
              <div className="flex items-center justify-between">
                <Badge variant={undefined} className={
                  review.status === "active" ? "bg-green-100 text-green-700" :
                    review.status === "archived" ? "bg-yellow-100 text-yellow-700" :
                      review.status === "deleted" ? "bg-red-100 text-red-700" :
                        review.status === "inactive" ? "bg-gray-100 text-gray-700" :
                          "bg-gray-100 text-gray-700"
                }>
                  {review.status.toUpperCase()}
                </Badge>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleContactCustomer}>
                    <Mail className="h-4 w-4 mr-2" />
                    Contact Customer
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleContactProvider}>
                    <Mail className="h-4 w-4 mr-2" />
                    Contact Provider
                  </Button>
                </div>
              </div>

              {/* User Information */}
              <div className="space-y-4">
                <h3 className="font-medium">User Information</h3>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{review.user.name}</p>
                      {review.user.verified && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          Verified User
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{review.user.totalReviews} total reviews</p>
                  </div>
                </div>
              </div>

              {/* Service Provider Information */}
              <div className="space-y-4">
                <h3 className="font-medium">Service Provider</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{review.provider.name}</p>
                      <p className="text-sm text-gray-500">{review.provider.service}</p>
                    </div>
                    <Badge variant={undefined} className={
                      review.provider.type === "Local Service"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-purple-100 text-purple-700"
                    }>
                      {review.provider.type}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Review Content */}
              <div className="space-y-4">
                <h3 className="font-medium">Review</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => {
                        const isHalfFilled = review.rating >= star - 0.5 && review.rating < star;
                        const isFilled = review.rating >= star;
                        
                        return (
                          <div key={star} className="relative">
                            <Star
                              className={`h-5 w-5 ${
                                isFilled
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            />
                            {isHalfFilled && (
                              <div className="absolute left-0 top-0 w-1/2 h-full overflow-hidden">
                                <Star
                                  className="h-5 w-5 fill-yellow-400 text-yellow-400"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(review.date).toLocaleDateString()}
                    </span>
                  </div>
                  <h4 className="font-medium mb-2">{review.title}</h4>
                  <p className="text-gray-600">
                    {review.content && review.content.length > 200 ? (
                      <span>
                        {expandedReviews.has(review.id) ? review.content : `${review.content.substring(0, 200)}...`}
                        <button
                          onClick={() => toggleReviewExpansion(review.id)}
                          className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {expandedReviews.has(review.id) ? "Show less" : "See more"}
                        </button>
                      </span>
                    ) : (
                      review.content
                    )}
                  </p>

                  {(review.attachments.images > 0 || review.attachments.videos > 0) && (
                    <div className="flex gap-4 mt-4">
                      {review.attachments.images > 0 && (
                        <Badge variant="outline">
                          {review.attachments.images} Images
                        </Badge>
                      )}
                      {review.attachments.videos > 0 && (
                        <Badge variant="outline">
                          {review.attachments.videos} Videos
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Service Details */}
              <div className="space-y-4">
                <h3 className="font-medium">Service Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-500">Service Date</span>
                    <p className="font-medium">{review.serviceDetails.date}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-500">Order ID</span>
                    <p className="font-medium">{review.serviceDetails.orderId}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-500">Service Cost</span>
                    <p className="font-medium">{review.serviceDetails.cost}</p>
                  </div>
                </div>
              </div>

              {/* Review Status */}
              {review.reported && (
                <Alert className="bg-red-50 border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700">
                    This review has been reported: {review.reportReason}
                  </AlertDescription>
                </Alert>
              )}

              {/* Admin Notes */}
              <div className="space-y-4">
                <h3 className="font-medium">Admin Notes</h3>
                <div className="space-y-3">
                  {review.adminNotes && review.adminNotes.length > 0 ? (
                    review.adminNotes.map((note, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between text-sm text-gray-500 mb-1">
                          <span>Admin Note #{index + 1}</span>
                          <span>{new Date().toLocaleString()}</span>
                        </div>
                        <p className="text-gray-700">{note}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm italic">No admin notes yet</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    placeholder="Add a note..."
                    className="flex-1 px-4 py-2 border rounded-lg"
                    disabled={isUpdating}
                  />
                  <Button 
                    onClick={handleAddAdminNote}
                    disabled={!adminNote.trim() || isUpdating}
                  >
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Note"}
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-6 border-t">
                <div className="flex gap-2">
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-4 py-2 border rounded-lg"
                    disabled={isUpdating}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="archived">Archived</option>
                    <option value="deleted">Deleted</option>
                  </select>
                  <Button 
                    variant="outline" 
                    className="text-red-600 hover:bg-red-50 hover:border-red-300"
                    onClick={handleDeleteReview}
                    disabled={isUpdating}
                  >
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    {isUpdating ? "Deleting..." : "Delete Permanently"}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={onClose}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleStatusUpdate}
                    disabled={isUpdating}
                  >
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // Review Card Component
  const ReviewCard = ({ review }) => (
    <Card className="hover:shadow-md transition-shadow" style={{ textAlign: "left" }}>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
              {review.user.avatar ? (
                <img
                  src={review.user.avatar}
                  alt={review.user.name}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <User className="h-5 w-5 text-gray-600" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{review.user.name}</span>
                {review.user.verified && (
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    <Check className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => {
                  const isHalfFilled = review.rating >= star - 0.5 && review.rating < star;
                  const isFilled = review.rating >= star;
                  
                  return (
                    <div key={star} className="relative">
                      <Star
                        className={`h-4 w-4 ${
                          isFilled
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                      {isHalfFilled && (
                        <div className="absolute left-0 top-0 w-1/2 h-full overflow-hidden">
                          <Star
                            className="h-4 w-4 fill-yellow-400 text-yellow-400"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <Badge variant={undefined} className={
            review.status === "active" ? "bg-green-100 text-green-700" :
              review.status === "archived" ? "bg-yellow-100 text-yellow-700" :
                review.status === "deleted" ? "bg-red-100 text-red-700" :
                  review.status === "inactive" ? "bg-gray-100 text-gray-700" :
                    "bg-gray-100 text-gray-700"
          }>
            {review.status.toUpperCase()}</Badge>
        </div>

        {/* Service Provider */}
        <div className="flex justify-between items-center" style={{ textAlign: "left" }}>
          <div>
            <span className="text-sm text-gray-500">Service Provider</span>
            <p className="font-medium">{review.provider.name}</p>
            <p className="text-sm text-gray-600">{review.provider.service}</p>
          </div>
          <Badge variant={undefined} className={
            review.provider.type === "Local Service"
              ? "bg-blue-100 text-blue-700"
              : "bg-purple-100 text-purple-700"
          }>
            {review.provider.type}
          </Badge>
        </div>

        {/* Review Content */}
        <div>
          <h4 className="font-medium mb-2">{review.title}</h4>
          <p className="text-gray-600">
            {review.content && review.content.length > 200 ? (
              <span>
                {expandedReviews.has(review.id) ? review.content : `${review.content.substring(0, 200)}...`}
                <button
                  onClick={() => toggleReviewExpansion(review.id)}
                  className="ml-2 text-blue-600 hover:text-blue-800 font-medium"
                >
                  {expandedReviews.has(review.id) ? "Show less" : "See more"}
                </button>
              </span>
            ) : (
              review.content
            )}
          </p>
        </div>

        {/* Attachments & Date */}
        <div className="flex justify-between items-center text-sm text-gray-500">
          <div className="flex gap-3">
            {review.attachments.images > 0 && (
              <Badge variant="outline">
                {review.attachments.images} Images
              </Badge>
            )}
            {review.attachments.videos > 0 && (
              <Badge variant="outline">
                {review.attachments.videos} Videos
              </Badge>
            )}
          </div>
          <span>{new Date(review.date).toLocaleDateString()}</span>
        </div>

        {/* Actions */}
        <div className="pt-4 border-t flex justify-between items-center">
          <div className="flex items-center gap-3">
            <VoteDisplayButton
              reviewId={review.id}
              votes={review.votes || []}
              currentUserId={null} // Admin view, no current user context needed
              className=""
            />
            {review.reported && (
              <Badge variant="outline" className="text-red-600 border-red-200">
                <Flag className="h-3 w-3 mr-1" />
                Reported
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReviewDetails(review)}
              className="text-blue-600"
            >
              View Details
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />
      <main className="flex-1 lg:pl-64 pt-16" style={{ textAlign: "left" }}>
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Reviews & Ratings</h1>
                <p className="text-gray-600 mt-1">Manage and monitor user reviews across all services</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export Reviews
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Reviews</p>
                    <p className="text-2xl font-semibold">{totalCount || 0}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Average Rating</p>
                    <p className="text-2xl font-semibold">
                      {reviews && reviews.length > 0
                        ? (reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length).toFixed(1)
                        : "0.0"
                      }
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Star className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Deleted Reviews</p>
                    <p className="text-2xl font-semibold">
                      {reviews ? reviews.filter(review => Number(review.status?.id) === ReviewStatusEnum.Deleted).length : 0}
                    </p>
                  </div>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Flag className="h-5 w-5 text-red-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Active Reviews</p>
                    <p className="text-2xl font-semibold">
                      {reviews ? reviews.filter(review => Number(review.status?.id) === ReviewStatusEnum.Active).length : 0}
                    </p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
                  <div className="relative flex-1 w-full sm:w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search reviews..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full lg:w-auto">
                  <select
                    value={selectedRating}
                    onChange={(e) => setSelectedRating(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Ratings</option>
                    <option value="5">5 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="3">3 Stars</option>
                    <option value="2">2 Stars</option>
                    <option value="1">1 Star</option>
                  </select>

                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="archived">Archived</option>
                    <option value="deleted">Deleted</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="rating-high">Highest Rating</option>
                    <option value="rating-low">Lowest Rating</option>
                    <option value="most-helpful">Most Helpful</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Reviews Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Loading reviews...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-red-500 mb-4">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-2" />
                  <p className="text-lg font-medium">Error loading reviews</p>
                  <p className="text-sm text-gray-600">{error}</p>
                </div>
                <Button
                  onClick={() => dispatch(fetchReviews({
                    page: currentPage,
                    limit: reviewsPerPage
                  }))}
                  variant="outline"
                >
                  Try Again
                </Button>
              </div>
            ) : displayReviews.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900">No reviews found</p>
                <p className="text-gray-600 mt-1">There are no reviews to display at the moment.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {displayReviews.map(review => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {shouldShowPagination && (
              <div className="mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 py-4 border-t">
                <div className="text-sm text-gray-500">
                  Showing {((currentPage - 1) * reviewsPerPage) + 1} to {Math.min(currentPage * reviewsPerPage, totalCount || 0)} of {totalCount || 0} reviews
                </div>

                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPreviousPage || loading}
                    className="flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {/* Show first page */}
                    {currentPage > 3 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(1)}
                          disabled={loading}
                        >
                          1
                        </Button>
                        {currentPage > 4 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                      </>
                    )}

                    {/* Show pages around current page */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      if (pageNum < 1 || pageNum > totalPages) return null;

                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className={currentPage === pageNum ? "bg-blue-600 text-white" : ""}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}

                    {/* Show last page */}
                    {currentPage < totalPages - 2 && (
                      <>
                        {currentPage < totalPages - 3 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(totalPages)}
                          disabled={loading}
                        >
                          {totalPages}
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Next Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPageCalculated || loading}
                    className="flex items-center gap-1"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Review Details Modal */}
      <ReviewDetailsModal
        review={showReviewDetails}
        isOpen={!!showReviewDetails}
        onClose={() => setShowReviewDetails(null)}
      />

      {/* <SharedFooter3 /> */}
    </div>
  );
};
export default ReviewsAndRatings;